import React, { FunctionComponent, useEffect, useState } from 'react'
import { SKUSelectorsResponse } from '@buy/components/VolumeComponentWrapper'
import { BoxWrapper, CardSelector } from '@common/components'
import { get, has, isEmpty, isUndefined } from 'lodash-es'
import { Addon } from '@buy/components/Addon'
import { BuyAddonPrice } from '@buy/state/price'
import { BillingDetailedSubscriptionPlan } from '@common/state/billing/state'
import { OrderSummaryLine } from '@buy/state/order-summary/order-summary'
import { remapPricingObject } from '@buy/utils/volume-components-helpers'
import { getCheckoutSessionState, SessionKeys, setCheckoutSessionState } from '@buy/utils'
import { ParamTypes } from '@buy/Routes'
import { useParams } from 'react-router-dom'
import { getUsagePriceType, isPayGoUsagePrice } from '@buy/utils/get-usage-price-values'
import styles from '../../connectedComponents/Customize/customize.module.css'
import { BillingTenureValue } from '../BillingPeriodSwitch'

interface AddonsWrapperProps {
    skuSelectors: Array<SKUSelectorsResponse>
    labels: LabelsType
    currencyCode: string
    isYearlyBillingFrequency: boolean
    locale: string
    accountSubscriptions: BillingDetailedSubscriptionPlan[]
    baseLine: OrderSummaryLine
    additionalLines: Array<OrderSummaryLine>
    addonLines: Array<OrderSummaryLine>
    setAddonLines: (value: Array<OrderSummaryLine>) => void
    volumeSelectorValues: Record<string, string | number>
    legacyAddons: Array<SKUSelectorsResponse>
    billingTenure: BillingTenureValue
}

export interface SkuAddonPrice {
    [sku: string]: BuyAddonPrice
}

export interface LabelsType {
    title: string
    description: string
    year: string
    month: string
    perLabel: string
}

export const AddonsWrapper: FunctionComponent<AddonsWrapperProps> = ({
    skuSelectors,
    labels,
    accountSubscriptions,
    volumeSelectorValues,
    currencyCode,
    isYearlyBillingFrequency,
    legacyAddons,
    ...rest
}) => {
    const { pathProduct, plan } = useParams<ParamTypes>()
    const [addons, setAddons] = useState<Array<SKUSelectorsResponse>>([])
    const [selectedValues, setSelectedValues] = useState<Set<string>>(new Set()) // array of skus

    const loadSessionState = () => {
        const sessionState = getCheckoutSessionState(pathProduct!, plan!)
        const hasSessionValue = !isEmpty(sessionState) && !isEmpty(get(sessionState, SessionKeys.addonFields))
        if (hasSessionValue) {
            const addonFields = get(sessionState, SessionKeys.addonFields)!
            const selectedValuesSet = new Set<string>()
            Object.keys(addonFields).forEach((key: string) => {
                if (addonFields[key]) selectedValuesSet.add(key)
            })
            setSelectedValues(selectedValuesSet)
        }
    }

    useEffect(() => {
        // load addons from legacy plans.addons node
        if (!isEmpty(legacyAddons)) {
            setAddons([...legacyAddons])
        } else {
            // load addons from skuSelector node
            const filteredAddons = skuSelectors.filter(
                (item) => !item.fixedQuantity && isEmpty(item.includedInSku) && isEmpty(item.volumeSelector)
            )
            setAddons([...filteredAddons])
        }
        loadSessionState()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skuSelectors])

    useEffect(() => {
        if (!isEmpty(volumeSelectorValues)) {
            const updatedAddonLines = rest.addonLines.map((item) => {
                const newItem = { ...item }
                const calculatedQuantity = volumeSelectorValues[item.unit!]
                    ? Number(volumeSelectorValues[item.unit!])
                    : 0
                const usagePriceType = getUsagePriceType(item.allPrices, item.usagePrice)
                newItem.quantity = isPayGoUsagePrice(usagePriceType) ? 1 : calculatedQuantity
                return newItem
            })
            rest.setAddonLines([...updatedAddonLines])
            setCheckoutSessionState(updatedAddonLines, SessionKeys.addonLines, pathProduct!, plan!)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [volumeSelectorValues])

    const onChangeAddon = (isSelected: boolean, key: string) => {
        setSelectedValues((oldState) => {
            if (isSelected) oldState.add(key)
            else oldState.delete(key)
            return new Set(oldState)
        })
    }

    const getLinkedSelectorValue = (unitName: string) => {
        if (isEmpty(volumeSelectorValues) || isUndefined(volumeSelectorValues[unitName])) {
            const basePlanSku: SKUSelectorsResponse = skuSelectors.find((item) => has(item, 'includedInSku'))!
            if (!isEmpty(basePlanSku)) {
                const basePlanUnit = basePlanSku.includedInSku!.find((item) => item.unit.unitName === unitName)
                return !isEmpty(basePlanUnit) ? basePlanUnit.quantity : 0
            }
        } else if (!isUndefined(volumeSelectorValues[unitName])) return volumeSelectorValues[unitName]
        return 0
    }

    return (
        <>
            {!isEmpty(addons) ? (
                <BoxWrapper data-qat="plan-addons-selection">
                    <h3>{labels.title}:</h3>
                    <div className={styles.description}>{labels.description}</div>
                    <CardSelector wrap={true}>
                        {addons.map((item) => {
                            const pricingInfo = remapPricingObject(item.pricingInfo, currencyCode, [])
                            return (
                                <Addon
                                    {...item}
                                    key={item.sku}
                                    isYearlyBillingFrequency={isYearlyBillingFrequency}
                                    labels={labels}
                                    currencyCode={currencyCode}
                                    locale={rest.locale}
                                    isDecimal={item.displayPriceWithDecimal}
                                    onChange={onChangeAddon}
                                    isSelected={selectedValues.has(item.sku)}
                                    icon={item.icon || ''}
                                    isDisabled={
                                        !isEmpty(accountSubscriptions?.find((value) => value.productSku === item.sku))
                                    }
                                    addonLines={rest.addonLines}
                                    setAddonLines={rest.setAddonLines}
                                    linkedSelectorValue={getLinkedSelectorValue(item.unit.unitName)}
                                    prices={pricingInfo.allPrices}
                                    usagePrice={pricingInfo.usagePrice}
                                    billingTenure={rest.billingTenure}
                                />
                            )
                        })}
                    </CardSelector>
                </BoxWrapper>
            ) : (
                <></>
            )}
        </>
    )
}
